import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import {StyledPolicies} from './StyledPolicies';
import Spin from '../../../../Spin';
import PolicyEditorWithPreview from '../../tabComponents/PolicyEditorWithPreview';
import {useFeatureAvailable} from '../../../../../hooks';
import {agentActions} from '../../../../../state/actions/agent.actions';
import {alertActions} from '../../../../../state/actions/alert.actions';
import {companyFeaturesConstants} from '../../../../../constants';

const policyKeys = {
  expense_policy: 'expense_policy',
  approval_policy: 'approval_policy',
  procurement_policy: 'procurement_policy',
}

const {approval_policy, expense_policy} = policyKeys;

// Default policies used when caller doesn't provide custom ones
const defaultPolicies = [
  { key: expense_policy },
  { alert: true, key: approval_policy }
];

const Policies = ({
  dispatch,
  createCompanyPolicy,
  getCompanyPolicies,
  updateCompanyPolicy,
  policies: customPolicies,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'settings']);

  // Use custom policies if provided, otherwise fall back to defaults
  const policiesList = (Array.isArray(customPolicies) && customPolicies.length)
    ? customPolicies
    : defaultPolicies;

  const [policy, setPolicy] = React.useState(null);
  // derive loading keys from policies list so custom keys are supported
  const [loading, setLoading] = React.useState(
    Object.fromEntries(policiesList.map(p => [p.key, true]))
  );

  const isEnableEdit = useFeatureAvailable(companyFeaturesConstants.USE_CUSTOM_POLICIES);

  const finishLoading = () => setLoading(Object.fromEntries(policiesList.map(p => [p.key, false])));

  useEffect(() => {
    getCompanyPolicies({
      successCallback: (resp) => {
        const policy = resp?.results?.[0] || undefined;
        if (policy) setPolicy(policy);
        finishLoading();
      },
      errorCallback: finishLoading,
    })
  }, [getCompanyPolicies]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdatePolicy = ({key, value, successCallback}) => {
    const id = policy?.id || null;
    let data = {[key]: value};

    if (!id) {
      // When creating new policy, ensure other policy keys are present (use policiesList)
      policiesList.forEach(p => {
        if (p.key !== key) data[p.key] = '';
      });
    }

    const action = id ? updateCompanyPolicy : createCompanyPolicy;

    setLoading({...loading, [key]: true});
    const dataToSend = id
      ? data
      : {
          ...Object.fromEntries(Object.keys(policyKeys).map(k => [k, ''])),
          ...data,
        };

    setLoading({...loading, [key]: true});
    action({
      ...(id ? {id} : {}),
      data: dataToSend,
      successCallback: (resp) => {
        setPolicy(resp);
        finishLoading();
        successCallback?.(resp);
      },
      errorCallback: () => {
        finishLoading();
        alertActions.error(t('settings:validation.failedToUpdatePolicy'));
      },
    });
  }

  const tabT = (key) => t(`settings:tabs.expenses.items.${key}`);

  // Render using the resolved policies list
  return (
    <StyledPolicies
      {...rest}
    >
      <Space style={{ width: '100%' }} size='middle' direction='vertical'>
        {policiesList.map(({alert, key}) => (
          <Spin key={key} spinning={loading[key]} size='small'>
            <PolicyEditorWithPreview
              alert={alert && tabT(`${key}.alert`)}
              enableEdit={isEnableEdit}
              defaultValue={policy?.[key] || ''}
              empty={tabT(`${key}.empty`) }
              title={tabT(`${key}.title`) }
              onSave={(props) => handleUpdatePolicy({key, ...props})}
            />
          </Spin>
        ))}
      </Space>
    </StyledPolicies>
  );
}

const mapDispatchToProps = {
  createCompanyPolicy: agentActions.createCompanyPolicy,
  getCompanyPolicies: agentActions.getCompanyPolicies,
  updateCompanyPolicy: agentActions.updateCompanyPolicy,
}

export default connect(null, mapDispatchToProps)(Policies);
